import { createContext, useContext, type FC, type ReactNode } from 'react';
import type { CustomChartTableView } from '@/entities/dashboards';

/**
 * Куда пишется вид колонок таблиц диаграмм: у применённого дашборда — по правилам его фильтров
 * (в сам дашборд либо за смотрящим поверх вида дашборда), у правящегося — в черновик состава.
 *
 * Идёт контекстом, а не пропом: таблицу рисует представление типа глубоко внутри карточки, и проп
 * прошёл бы через все представления, которым вид колонок не нужен.
 */
export type ChartTableViewStore = {
  /** Свой вид колонок смотрящего поверх вида дашборда; нет — таблица показана видом дашборда */
  readOwnView: (chartId: string) => CustomChartTableView | undefined;
  /** «Очистить» в панели колонок возвращает к виду дашборда, а не показывает все колонки выборки */
  clearsToDashboardView: boolean;
  /** Пользователь сменил вид колонок таблицы */
  onChange: (chartId: string, view: CustomChartTableView) => void;
  /** «Очистить» в панели колонок */
  onClear: (chartId: string) => void;
};

const ChartTableViewContext = createContext<ChartTableViewStore | null>(null);

type Props = {
  store: ChartTableViewStore;
  children: ReactNode;
};

/** Задаёт запись вида колонок таблицам диаграмм, отрисованным внутри. */
export const ChartTableViewProvider: FC<Props> = ({ store, children }) => (
  <ChartTableViewContext.Provider value={store}>{children}</ChartTableViewContext.Provider>
);

/** Запись вида колонок таблиц текущего дашборда; `null` — вид колонок никуда не пишется. */
export const useChartTableViewStore = (): ChartTableViewStore | null => useContext(ChartTableViewContext);
